
import React, { useState, useEffect, useRef } from 'react';
import { SiteTheme } from '../../types';
import { Bean } from 'lucide-react';

interface BrandLogoProps {
    variant?: 'dark' | 'light' | 'color'; // 'color' uses dark logo by default
    className?: string;
    layout?: 'boxed' | 'minimal'; // Kept for prop compatibility
    size?: 'sm' | 'md' | 'lg' | 'xl';
    theme?: SiteTheme; // Allow passing theme configuration
    /** Tona in när bilden laddats. Inte i utskrifter: där kopieras HTML:en
     *  som den är, och en osynlig logga skulle följa med. */
    fadeIn?: boolean;
}

export const BrandLogo: React.FC<BrandLogoProps> = ({ variant = 'dark', className = '', size = 'md', theme, fadeIn = false }) => {
    const [hasError, setHasError] = useState(false);
    // Loggan tonas in när bilden laddats istället för att poppa upp.
    // Redan cachad bild (complete) visas direkt.
    const imgRef = useRef<HTMLImageElement>(null);
    const [loaded, setLoaded] = useState(false);
    
    const isLight = variant === 'light';
    // Reverted to specific Supabase URL as requested
    const fallbackUrl = 'https://safpfosbbrqmexlhjrth.supabase.co/storage/v1/object/public/products/logo-1767917522580-hccoe5akwhm.png'; 
    
    // Logic:
    // 1. If Light Variant: Try Theme White -> Fallback to Theme Dark -> Fallback to URL
    // 2. If Dark Variant: Try Theme Dark -> Fallback to URL
    
    let imgSrc = fallbackUrl;

    if (isLight) {
        if (theme?.logoWhiteUrl) imgSrc = theme.logoWhiteUrl;
        else if (theme?.logoUrl) imgSrc = theme.logoUrl; 
    } else {
        if (theme?.logoUrl) imgSrc = theme.logoUrl;
    }

    // Reset error state if source changes
    useEffect(() => {
        setHasError(false);
        const img = imgRef.current;
        setLoaded(!!img && img.complete && img.naturalWidth > 0);
    }, [imgSrc]);

    const sizeClasses = {
        sm: 'h-6',
        md: 'h-8',
        lg: 'h-12',
        xl: 'h-20'
    };

    // If theme provides specific height, override class
    const heightStyle = theme?.logoHeight ? { height: `${theme.logoHeight}px` } : {};
    const heightClass = theme?.logoHeight ? '' : (sizeClasses[size] || sizeClasses.md);

    // Fallback UI (Text + Icon) - Only used if image load fails completely
    if (hasError) {
        const textColor = isLight ? 'text-white' : 'text-[#1A120B]';
        const iconColor = isLight ? 'text-white/80' : 'text-[#816358]';
        
        return (
            <div className={`inline-flex items-center gap-2 select-none ${className}`}>
                <div 
                    className={`aspect-square rounded-lg bg-current opacity-10 flex items-center justify-center ${heightClass}`}
                    style={heightStyle}
                >
                </div>
                <div 
                    className={`absolute aspect-square flex items-center justify-center ${iconColor} ${heightClass}`}
                    style={heightStyle}
                >
                    <Bean size="70%" strokeWidth={2} fill="currentColor" />
                </div>
                <span className={`font-serif font-bold tracking-wide uppercase ${textColor} ${size === 'sm' ? 'text-sm' : size === 'xl' ? 'text-3xl' : 'text-lg'}`}>
                    CoffeeNova
                </span>
            </div>
        );
    }

    return (
        <div className={`inline-flex items-center justify-center select-none ${className}`}>
            <img 
                ref={imgRef}
                src={imgSrc} 
                alt="Brand Logo" 
                className={`w-auto max-w-full object-contain transition-opacity duration-500 ${!fadeIn || loaded ? 'opacity-100' : 'opacity-0'} ${heightClass}`}
                style={heightStyle}
                onLoad={() => setLoaded(true)}
                onError={() => setHasError(true)}
            />
        </div>
    );
};
